import { signInAnonymously } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { auth, db, functions, storage } from "../firebase";
import {
  defaultPhoenixSpaces,
  phoenixHubContent,
  PHOENIX_VENUE_ID,
} from "./config";

const useDefaultSpaces =
  process.env.REACT_APP_PHOENIX_USE_DEFAULT_SPACES === "true" ||
  process.env.REACT_APP_FIREBASE_PROJECT_ID === "urban-unity-local";

function snapshotRows(snapshot) {
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function loadPhoenixSpaces({ includeInactive = false } = {}) {
  if (useDefaultSpaces) {
    return includeInactive
      ? defaultPhoenixSpaces
      : defaultPhoenixSpaces.filter((space) => space.active);
  }
  try {
    const constraints = includeInactive
      ? [orderBy("displayOrder", "asc")]
      : [where("active", "==", true), orderBy("displayOrder", "asc")];
    const snapshot = await getDocs(query(collection(db, "phoenixSpaces"), ...constraints));
    return snapshot.empty ? defaultPhoenixSpaces : snapshotRows(snapshot);
  } catch (error) {
    console.warn("Using Phoenix Hub space defaults until Firestore spaces are seeded.", error);
    return includeInactive ? defaultPhoenixSpaces : defaultPhoenixSpaces.filter((space) => space.active);
  }
}

export async function checkPhoenixAvailability(payload) {
  const check = httpsCallable(functions, "checkPhoenixAvailability");
  const result = await check({
    venueId: PHOENIX_VENUE_ID,
    selectedSpaceIds: payload.selectedSpaceIds,
    requestedDates: payload.requestedDates,
  });
  return result.data;
}

function safeFileName(fileName) {
  const dot = fileName.lastIndexOf(".");
  const extension = dot >= 0 ? fileName.slice(dot).toLowerCase() : "";
  const base = (dot >= 0 ? fileName.slice(0, dot) : fileName)
    .normalize("NFKD")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `${base || "document"}${extension}`;
}

async function ensureUploadIdentity() {
  if (auth.currentUser) return auth.currentUser;
  const credential = await signInAnonymously(auth);
  return credential.user;
}

async function uploadBookingDocuments(bookingId, files) {
  if (!files.length) return [];
  await ensureUploadIdentity();
  const uploaded = [];
  for (const file of files) {
    const uniqueName = `${crypto.randomUUID()}-${safeFileName(file.name)}`;
    const path = `phoenixBookings/${bookingId}/documents/${uniqueName}`;
    await uploadBytes(storageRef(storage, path), file, {
      contentType: file.type,
      customMetadata: { originalName: file.name },
    });
    uploaded.push({
      path,
      originalName: file.name,
      contentType: file.type,
      size: file.size,
    });
  }
  return uploaded;
}

export async function submitPhoenixBooking(payload, files = [], idempotencyKey = "") {
  if (files.length && !phoenixHubContent.documentUploadsEnabled) {
    throw new Error("Document uploads are not enabled for this deployment.");
  }
  if (files.length) await ensureUploadIdentity();
  const submit = httpsCallable(functions, "submitPhoenixBooking");
  const result = await submit({ ...payload, idempotencyKey });
  const response = result.data;
  let uploadWarning = "";
  if (files.length) {
    try {
      const documents = await uploadBookingDocuments(response.bookingId, files);
      const attach = httpsCallable(functions, "attachPhoenixBookingDocuments");
      await attach({ bookingId: response.bookingId, documents });
    } catch (error) {
      console.error("Phoenix booking documents could not be uploaded.", error);
      uploadWarning =
        "Your request was received, but one or more documents could not be attached. Please quote your reference when contacting UUN.";
    }
  }
  return { ...response, uploadWarning };
}

export function subscribePhoenixBookings(onData, onError) {
  return onSnapshot(
    query(collection(db, "phoenixBookingRequests"), orderBy("submittedAt", "desc")),
    (snapshot) => onData(snapshotRows(snapshot)),
    onError
  );
}

export function subscribePhoenixBooking(bookingId, onData, onError) {
  return onSnapshot(
    doc(db, "phoenixBookingRequests", bookingId),
    (snapshot) => onData(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null),
    onError
  );
}

export function subscribePhoenixAuditLog(bookingId, onData, onError) {
  return onSnapshot(
    query(
      collection(db, "phoenixBookingAuditLog"),
      where("bookingId", "==", bookingId),
      orderBy("createdAt", "desc")
    ),
    (snapshot) => onData(snapshotRows(snapshot)),
    onError
  );
}

export function subscribePhoenixBlocks(onData, onError) {
  return onSnapshot(
    query(collection(db, "phoenixBlockedPeriods"), orderBy("startDateTime", "asc")),
    (snapshot) => onData(snapshotRows(snapshot)),
    onError
  );
}

export function subscribePhoenixSpaces(onData, onError) {
  if (useDefaultSpaces) {
    onData(defaultPhoenixSpaces);
    return () => {};
  }
  return onSnapshot(
    query(collection(db, "phoenixSpaces"), orderBy("displayOrder", "asc")),
    (snapshot) => onData(snapshot.empty ? defaultPhoenixSpaces : snapshotRows(snapshot)),
    onError
  );
}

export async function updatePhoenixBooking(bookingId, changes, action = "booking_updated") {
  const user = auth.currentUser;
  const {
    auditNote = "",
    auditPreviousStatus = null,
    auditNewStatus = null,
    ...bookingChanges
  } = changes;
  const batch = writeBatch(db);
  batch.update(doc(db, "phoenixBookingRequests", bookingId), {
    ...bookingChanges,
    updatedAt: serverTimestamp(),
  });
  const audit = doc(collection(db, "phoenixBookingAuditLog"));
  batch.set(audit, {
    bookingId,
    action,
    performedBy: user?.uid || "",
    performedByEmail: user?.email || "",
    previousStatus: auditPreviousStatus,
    newStatus: auditNewStatus,
    notes: auditNote,
    createdAt: serverTimestamp(),
  });
  await batch.commit();
}

export async function changePhoenixBookingStatus(booking, newStatus, reason = "") {
  if (["rejected", "cancelled"].includes(newStatus) && !reason.trim()) {
    throw new Error("A reason is required for rejection or cancellation.");
  }
  const timestampField = {
    under_review: "reviewedAt",
    approved_awaiting_payment: "approvedAt",
    confirmed: "confirmedAt",
    rejected: "rejectedAt",
    cancelled: "cancelledAt",
  }[newStatus];
  const changes = {
    status: newStatus,
    statusReason: reason.trim(),
    reviewedBy: auth.currentUser?.uid || "",
    provisionalHold: newStatus === "provisional_hold",
    auditPreviousStatus: booking.status,
    auditNewStatus: newStatus,
    auditNote: reason.trim(),
  };
  if (timestampField) changes[timestampField] = serverTimestamp();
  await updatePhoenixBooking(booking.id, changes, "status_changed");
}

export async function savePhoenixSpace(space) {
  const id = space.id || space.slug;
  await setDoc(
    doc(db, "phoenixSpaces", id),
    {
      ...space,
      id,
      venueId: PHOENIX_VENUE_ID,
      hourlyRate: space.hourlyRate === "" ? null : Number(space.hourlyRate),
      capacity: space.capacity === "" ? null : Number(space.capacity),
      displayOrder: Number(space.displayOrder) || 0,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function seedDefaultPhoenixSpaces() {
  const batch = writeBatch(db);
  defaultPhoenixSpaces.forEach((space) => {
    batch.set(doc(db, "phoenixSpaces", space.id), {
      ...space,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
}

export async function uploadPhoenixSpaceImage(spaceId, file) {
  const path = `phoenixSpaces/${spaceId}/images/${crypto.randomUUID()}-${safeFileName(file.name)}`;
  const reference = storageRef(storage, path);
  await uploadBytes(reference, file, { contentType: file.type });
  return { path, url: await getDownloadURL(reference), alt: "" };
}

export async function savePhoenixBlock(block) {
  const payload = {
    ...block,
    venueId: PHOENIX_VENUE_ID,
    updatedAt: serverTimestamp(),
    createdBy: auth.currentUser?.uid || "",
  };
  if (block.id) {
    const { id, ...changes } = payload;
    await updateDoc(doc(db, "phoenixBlockedPeriods", id), changes);
    return id;
  }
  const result = await addDoc(collection(db, "phoenixBlockedPeriods"), {
    ...payload,
    createdAt: serverTimestamp(),
  });
  return result.id;
}

export async function deletePhoenixBlock(blockId) {
  await deleteDoc(doc(db, "phoenixBlockedPeriods", blockId));
}

export async function getPrivateDocumentUrl(path) {
  return getDownloadURL(storageRef(storage, path));
}

export async function uploadPhoenixInternalDocument(bookingId, file) {
  const path = `phoenixBookings/${bookingId}/internal/${crypto.randomUUID()}-${safeFileName(file.name)}`;
  await uploadBytes(storageRef(storage, path), file, { contentType: file.type });
  return path;
}
